import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { OrdnerPickerModal } from "@/components/filter/OrdnerPickerModal";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import { Spinner } from "@/components/ui/Spinner";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useStatusSichtbarkeit } from "@/hooks/useStatusSichtbarkeit";
import type { CurrentUser } from "@/lib/auth";
import { userIsAbrechnung, userIsBuero, userIsManagement } from "@/lib/auth";
import {
  listKundenForYear,
  listOrdner,
  listOrdnerForYearCustomer,
  listSOrdner,
  type OrdnerOut,
  type SOrdnerOut,
} from "@/lib/projects";
import {
  ordnerChipNeedsPrefixBreak,
  sortOrdnersForChipLayout,
  sortSOrdnersForChipLayout,
  sOrdnerChipNeedsDecadeBreak,
} from "@/lib/ordnerChipLayout";
import { listBillingMembers, type BillingMember, type TimesheetSummary } from "@/lib/timesheets";
import {
  BILLING_STATUS_OPTIONS,
  deriveSOrdnerChips,
  getTimesheetYears,
  STATUS_FILTER_OPTIONS,
} from "@/pages/stundennachweise/timesheetFilterLogic";
import type { TimesheetFilters } from "@/pages/stundennachweise/useTimesheetFilters";
import { timesheetHasActiveFilters } from "@/pages/stundennachweise/useTimesheetFilters";

export interface FilterBarProps {
  filters: TimesheetFilters;
  timesheets: TimesheetSummary[];
  user: CurrentUser | null;
  onChange: (patch: Partial<TimesheetFilters>) => void;
  onReset?: () => void;
  /**
   * Verfügbare Jahre (aus den Server-Facetten). Wenn gesetzt, werden die
   * Jahres-Chips daraus gebildet, statt sie aus der geladenen Liste abzuleiten –
   * nötig, weil bei serverseitiger Begrenzung nur das aktive Jahr geladen wird.
   */
  availableYears?: number[];
}

function ordnerOption(o: OrdnerOut): AutocompleteOption<OrdnerOut> {
  const meta = [o.kunden_name, o.beschreibung].filter(Boolean).join(" · ");
  return {
    key: String(o.id),
    label: o.nummer,
    description: meta || undefined,
    value: o,
  };
}

function dedupeOrdnersByJahrNummer(list: OrdnerOut[]): OrdnerOut[] {
  const seen = new Set<string>();
  const out: OrdnerOut[] = [];
  for (const o of list) {
    const key = `${o.jahr}\u0000${o.nummer.trim().toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(o);
  }
  return out;
}

function dedupeOrdnerIds(list: OrdnerOut[]): OrdnerOut[] {
  const seen = new Set<number>();
  const out: OrdnerOut[] = [];
  for (const o of list) {
    if (seen.has(o.id)) continue;
    seen.add(o.id);
    out.push(o);
  }
  return out;
}

function dedupeSOrdnerIds(list: SOrdnerOut[]): SOrdnerOut[] {
  const seen = new Set<number>();
  const out: SOrdnerOut[] = [];
  for (const s of list) {
    if (seen.has(s.id)) continue;
    seen.add(s.id);
    out.push(s);
  }
  return out;
}

export function FilterBar({
  filters,
  timesheets,
  user,
  onChange,
  onReset,
  availableYears,
}: FilterBarProps) {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const years = useMemo(
    () => (availableYears && availableYears.length ? availableYears : getTimesheetYears(timesheets)),
    [availableYears, timesheets],
  );
  const [kunden, setKunden] = useState<string[]>([]);
  const [kundenLoading, setKundenLoading] = useState(false);
  const [ordner, setOrdner] = useState<OrdnerOut[]>([]);
  const [ordnerLoading, setOrdnerLoading] = useState(false);
  const [sordner, setSOrdner] = useState<SOrdnerOut[]>([]);
  const [sordnerLoading, setSordnerLoading] = useState(false);
  const [ordnerModalOpen, setOrdnerModalOpen] = useState(false);
  const [sordnerQuickFilter, setSordnerQuickFilter] = useState("");
  const hasActiveFilters = timesheetHasActiveFilters(filters);
  const showFilterBody = !isMobile || filtersExpanded;

  useEffect(() => {
    let cancelled = false;
    if (filters.jahr === "S" || filters.jahr === "all") {
      setKunden([]);
      return;
    }
    if (typeof filters.jahr !== "number") return;

    setKundenLoading(true);
    listKundenForYear(filters.jahr)
      .then((list) => {
        if (!cancelled) setKunden(list);
      })
      .catch(() => {
        if (!cancelled) setKunden([]);
      })
      .finally(() => {
        if (!cancelled) setKundenLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters.jahr]);

  useEffect(() => {
    let cancelled = false;
    if (filters.jahr === "S" || filters.jahr === "all") {
      setOrdner([]);
      return;
    }
    if (typeof filters.jahr !== "number") return;
    const jahr = filters.jahr;

    setOrdnerLoading(true);
    void (async () => {
      try {
        const list = filters.kunde
          ? await listOrdnerForYearCustomer(filters.kunde, jahr)
          : await listOrdner(jahr);
        if (!cancelled) setOrdner(list);
      } catch {
        if (!cancelled) setOrdner([]);
      } finally {
        if (!cancelled) setOrdnerLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filters.jahr, filters.kunde]);

  useEffect(() => {
    let cancelled = false;
    if (filters.jahr !== "S") return;
    setSordnerLoading(true);
    listSOrdner()
      .then((list) => {
        if (!cancelled) setSOrdner(list);
      })
      .catch(() => {
        if (!cancelled) setSOrdner([]);
      })
      .finally(() => {
        if (!cancelled) setSordnerLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.jahr]);

  const ordnerDisplay = useMemo(
    () => sortOrdnersForChipLayout(dedupeOrdnersByJahrNummer(dedupeOrdnerIds(ordner))),
    [ordner],
  );

  const sortedSOrdner = useMemo(() => {
    const q = sordnerQuickFilter.trim().toLowerCase();
    const base =
      filters.jahr === "S" && sordner.length
        ? sortSOrdnersForChipLayout(dedupeSOrdnerIds(sordner))
        : sortSOrdnersForChipLayout(deriveSOrdnerChips(timesheets));
    if (!q) return base;
    return base.filter(
      (s) =>
        s.nummer.toLowerCase().includes(q) ||
        (s.beschreibung || "").toLowerCase().includes(q),
    );
  }, [filters.jahr, sordner, sordnerQuickFilter, timesheets]);

  const [billingMembers, setBillingMembers] = useState<BillingMember[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);

  const isAbrechnung = userIsAbrechnung(user);
  const showBillingFilter = isAbrechnung;
  const showRueckfrageFilter = isAbrechnung || userIsManagement(user);
  const showBueroStatus = userIsBuero(user) || isAbrechnung;
  const { visibleStatuses } = useStatusSichtbarkeit();

  useEffect(() => {
    if (!showBillingFilter) return;
    let cancelled = false;
    setBillingLoading(true);
    listBillingMembers()
      .then((list) => {
        if (!cancelled) setBillingMembers(list);
      })
      .catch(() => {
        if (!cancelled) setBillingMembers([]);
      })
      .finally(() => {
        if (!cancelled) setBillingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showBillingFilter]);

  const billingOptions = useMemo(() => {
    const byLogin = new Map<string, string>();
    billingMembers.forEach((member) => {
      const login =
        member.user_login || member.login || member.samAccountName || "";
      if (!login) return;
      const label = member.displayName
        ? `${member.displayName} (${login})`
        : login;
      if (!byLogin.has(login)) byLogin.set(login, label);
    });
    return [...byLogin.entries()].sort((a, b) => a[1].localeCompare(b[1], "de"));
  }, [billingMembers]);

  const statusOptions = useMemo(() => {
    const visible = new Set(visibleStatuses("stundennachweise"));
    let opts = STATUS_FILTER_OPTIONS;
    if (showBueroStatus) {
      const extra = BILLING_STATUS_OPTIONS;
      const base = STATUS_FILTER_OPTIONS.filter((s) => s.value !== "archived");
      const archived = STATUS_FILTER_OPTIONS.find((s) => s.value === "archived");
      opts = [...base, ...extra, ...(archived ? [archived] : [])];
    }
    return opts.filter((opt) => opt.value === "all" || visible.has(opt.value));
  }, [showBueroStatus, visibleStatuses]);

  const showKundeOrdner = typeof filters.jahr === "number";
  const showSOrdner = filters.jahr === "S";

  const selectedOrdner = useMemo(
    () => ordnerDisplay.find((o) => o.nummer === filters.ordner) ?? null,
    [ordnerDisplay, filters.ordner],
  );

  const selectedOrdnerOption = useMemo(
    () => (selectedOrdner ? ordnerOption(selectedOrdner) : null),
    [selectedOrdner],
  );

  const scrollRow =
    "-mx-0.5 flex flex-nowrap gap-2 overflow-x-auto overflow-y-hidden px-0.5 pb-1.5 [-webkit-overflow-scrolling:touch] [scrollbar-width:thin]";
  const wrapRow = "flex flex-wrap gap-2";
  const tileSurface = clsx(
    "rounded-ios-lg bg-fill-quaternary/20 px-4 py-3 shadow-ios md:px-5 md:py-4",
    "ring-1 ring-separator/30 dark:ring-separator/45",
  );

  return (
    <>
      {isMobile ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setFiltersExpanded((v) => !v)}
            className="flex min-h-11 flex-1 items-center justify-between gap-3 rounded-ios-lg bg-fill-quaternary/20 px-4 py-2.5 text-left ring-1 ring-separator/30 dark:ring-separator/45"
            aria-expanded={filtersExpanded}
          >
            <span className="text-sm font-semibold text-label">
              {filtersExpanded ? "Filter ausblenden" : "Filter anzeigen"}
            </span>
            <span className="text-xs text-label-secondary">
              {hasActiveFilters ? "Aktiv" : "Alle"}
            </span>
          </button>
          {hasActiveFilters && onReset ? (
            <button
              type="button"
              onClick={onReset}
              className="shrink-0 rounded-full bg-fill-tertiary px-3 py-2.5 text-sm font-semibold text-accent"
            >
              Zurücksetzen
            </button>
          ) : null}
        </div>
      ) : null}

      {showFilterBody ? (
      <div className="grid grid-cols-1 gap-3 md:gap-4 lg:gap-5 lg:[grid-template-columns:minmax(0,1fr)_minmax(0,1fr)] lg:items-stretch [&>*]:min-w-0">
        <div className={clsx(tileSurface, "flex min-h-[min-content] flex-col lg:min-h-0 lg:h-full")}>
          <Stack aria-label="Jahr filtern">
            <SectionLabel>Jahr</SectionLabel>
            <div className={scrollRow}>
              <ChipButton
                compact
                active={filters.jahr === "all"}
                onClick={() => onChange({ jahr: "all", kunde: "", ordner: "", sordner: "" })}
              >
                Alle
              </ChipButton>
              {years.map((y) => (
                <ChipButton
                  compact
                  key={y}
                  active={filters.jahr === y}
                  onClick={() =>
                    onChange({ jahr: y, kunde: "", ordner: "", sordner: "" })
                  }
                >
                  {y}
                </ChipButton>
              ))}
            </div>
            <div className={clsx(scrollRow, "mt-2")}>
              <ChipButton
                compact
                active={filters.jahr === "S"}
                onClick={() => onChange({ jahr: "S", kunde: "", ordner: "", sordner: "" })}
              >
                S‑Ordner
              </ChipButton>
            </div>
          </Stack>

          {showKundeOrdner ? (
            <Stack className="mt-3" aria-label="Kunde filtern">
              <SectionLabel
                right={
                  kundenLoading ? (
                    <Spinner className="h-3.5 w-3.5 text-label-tertiary" />
                  ) : null
                }
              >
                Kunde
              </SectionLabel>
              <div className={wrapRow}>
                <ChipButton
                  compact
                  active={filters.kunde === ""}
                  disabled={kundenLoading}
                  onClick={() => onChange({ kunde: "", ordner: "" })}
                >
                  Alle
                </ChipButton>
                {kunden.map((k) => (
                  <ChipButton
                    compact
                    key={k}
                    active={filters.kunde === k}
                    disabled={kundenLoading}
                    onClick={() =>
                      onChange({
                        kunde: filters.kunde === k ? "" : k,
                        ordner: "",
                      })
                    }
                    title={k}
                  >
                    {k}
                  </ChipButton>
                ))}
              </div>
            </Stack>
          ) : null}

          <Stack className="mt-3" aria-label="Status filtern">
            <SectionLabel>Status</SectionLabel>
            <div className={wrapRow}>
              {statusOptions.map((st) => (
                <ChipButton
                  compact
                  key={st.value}
                  active={filters.status === st.value}
                  onClick={() => onChange({ status: st.value })}
                >
                  {st.label}
                </ChipButton>
              ))}
            </div>
          </Stack>

          {showBillingFilter ? (
            <Stack className="mt-3" aria-label="Abrechnung filtern">
              <SectionLabel
                right={
                  billingLoading ? (
                    <Spinner className="h-3.5 w-3.5 text-label-tertiary" />
                  ) : null
                }
              >
                Abrechnung
              </SectionLabel>
              <select
                className="eh-input max-w-md text-sm"
                value={filters.billing || "all"}
                disabled={billingLoading}
                onChange={(e) => onChange({ billing: e.target.value || "all" })}
                aria-label="Abrechnung auswählen"
              >
                <option value="all">Alle Abrechnungen</option>
                {billingOptions.map(([login, label]) => (
                  <option key={login} value={login}>
                    {label}
                  </option>
                ))}
              </select>
            </Stack>
          ) : null}

          {showRueckfrageFilter ? (
            <Stack className="mt-3" aria-label="Rückfrage filtern">
              <SectionLabel>Rückfrage</SectionLabel>
              <div className={wrapRow}>
                <ChipButton
                  compact
                  active={!filters.rueckfrage}
                  onClick={() => onChange({ rueckfrage: false })}
                >
                  Alle
                </ChipButton>
                <ChipButton
                  compact
                  active={filters.rueckfrage}
                  onClick={() => onChange({ rueckfrage: true })}
                >
                  Mit Rückfrage
                </ChipButton>
              </div>
            </Stack>
          ) : null}

          <div className="mt-3 flex flex-wrap items-end gap-x-3 gap-y-2">
            {timesheetHasActiveFilters(filters) && onReset ? (
              <button
                type="button"
                onClick={onReset}
                className="shrink-0 text-sm font-semibold text-accent hover:underline"
              >
                Zurücksetzen
              </button>
            ) : null}
          </div>
        </div>

        <div className={clsx(tileSurface, "flex min-h-[min-content] flex-col lg:min-h-0 lg:h-full")}>
          {showKundeOrdner ? (
            <>
              <Stack aria-label="Ordner filtern">
                <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
                  <div className="min-w-[4rem] space-y-1">
                    <SectionLabel
                      right={
                        ordnerLoading ? (
                          <Spinner className="h-3.5 w-3.5 text-label-tertiary" />
                        ) : null
                      }
                    >
                      Ordner
                    </SectionLabel>
                    <span className="inline-block rounded-full bg-fill-tertiary px-2.5 py-1 text-xs font-medium text-label-secondary tabular-nums md:text-[13px]">
                      {ordnerLoading ? "…" : `${ordnerDisplay.length} Ordner`}
                    </span>
                  </div>
                  <div className="ml-auto flex shrink-0">
                    <button
                      type="button"
                      onClick={() => setOrdnerModalOpen(true)}
                      className="rounded-full bg-fill-tertiary px-4 py-2 text-sm font-medium text-label hover:bg-fill"
                    >
                      Liste&nbsp;&amp;&nbsp;Suche
                    </button>
                  </div>
                </div>
                <LockedAutocomplete<OrdnerOut>
                  placeholder="Ordner suchen …"
                  value={selectedOrdnerOption}
                  disabled={ordnerLoading}
                  minChars={0}
                  loadOptions={async (q) => {
                    const term = q.trim().toLowerCase();
                    return ordnerDisplay
                      .filter((o) =>
                        !term
                          ? true
                          : [o.nummer, o.beschreibung, o.kunden_name]
                              .filter(Boolean)
                              .some((s) => String(s).toLowerCase().includes(term)),
                      )
                      .map(ordnerOption);
                  }}
                  onChange={(opt) =>
                    onChange({
                      ordner: opt?.value.nummer ?? "",
                    })
                  }
                />
              </Stack>
              <div className="mt-3 max-h-[min(32vh,16rem)] min-w-0 flex-1 overflow-y-auto pr-1 md:max-h-[min(48vh,22rem)] [scrollbar-width:thin]">
                {ordnerLoading ? (
                  <div className="flex items-center gap-2 py-6 text-sm text-label-secondary">
                    <Spinner className="h-4 w-4" />
                    Ordner werden geladen …
                  </div>
                ) : ordnerDisplay.length === 0 ? (
                  <p className="py-4 text-sm text-label-tertiary">Keine Ordner für diese Auswahl.</p>
                ) : (
                  <div className="flex w-full min-w-0 flex-row flex-wrap items-center gap-x-2 gap-y-2.5">
                    <ChipButton compact active={!filters.ordner} onClick={() => onChange({ ordner: "" })}>
                      Alle
                    </ChipButton>
                    <div
                      aria-hidden
                      className="h-0 w-full shrink-0 grow-0 basis-full overflow-hidden"
                    />
                    {ordnerDisplay.flatMap((o, idx) => {
                      const prev = ordnerDisplay[idx - 1];
                      const needBreak = ordnerChipNeedsPrefixBreak(prev?.nummer, o.nummer);
                      const chip = (
                        <ChipButton
                          compact
                          key={o.id}
                          active={filters.ordner === o.nummer}
                          title={[o.kunden_name, o.beschreibung].filter(Boolean).join(" · ")}
                          onClick={() =>
                            onChange({
                              ordner: filters.ordner === o.nummer ? "" : o.nummer,
                            })
                          }
                        >
                          {o.nummer}
                        </ChipButton>
                      );
                      return needBreak
                        ? [
                            <div
                              key={`pref-br-${o.id}`}
                              aria-hidden
                              className="h-0 w-full shrink-0 grow-0 basis-full overflow-hidden"
                            />,
                            chip,
                          ]
                        : [chip];
                    })}
                  </div>
                )}
              </div>
            </>
          ) : null}

          {showSOrdner ? (
            <>
              <Stack aria-label="S-Ordner filtern" className={showKundeOrdner ? "mt-3" : undefined}>
                <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                  <SectionLabel
                    right={
                      sordnerLoading ? (
                        <Spinner className="h-3.5 w-3.5 text-label-tertiary" />
                      ) : null
                    }
                  >
                    S‑Ordner
                  </SectionLabel>
                  <input
                    type="search"
                    value={sordnerQuickFilter}
                    onChange={(e) => setSordnerQuickFilter(e.target.value)}
                    placeholder="S‑Ordner suchen …"
                    className="eh-input min-w-[12rem] max-w-full flex-1 py-2 text-sm"
                    aria-label="S-Ordner eingrenzen"
                  />
                </div>
              </Stack>
              <div className="mt-3 max-h-[min(32vh,16rem)] min-w-0 flex-1 overflow-y-auto pr-1 md:max-h-[min(48vh,22rem)] [scrollbar-width:thin]">
                {sordnerLoading ? (
                  <div className="flex items-center gap-2 py-6 text-sm text-label-secondary">
                    <Spinner className="h-4 w-4" />
                    S‑Ordner werden geladen …
                  </div>
                ) : sortedSOrdner.length === 0 ? (
                  <p className="py-4 text-sm text-label-tertiary">Keine S‑Ordner.</p>
                ) : (
                  <div className="flex w-full min-w-0 flex-row flex-wrap items-center gap-x-2 gap-y-2.5">
                    <ChipButton compact active={!filters.sordner} onClick={() => onChange({ sordner: "" })}>
                      Alle
                    </ChipButton>
                    <div
                      aria-hidden
                      className="h-0 w-full shrink-0 grow-0 basis-full overflow-hidden"
                    />
                    {sortedSOrdner.flatMap((s, idx) => {
                      const prev = sortedSOrdner[idx - 1];
                      const needBreak = sOrdnerChipNeedsDecadeBreak(prev?.nummer, s.nummer);
                      const chip = (
                        <ChipButton
                          compact
                          key={s.nummer}
                          active={filters.sordner === s.nummer}
                          title={s.beschreibung || s.nummer}
                          onClick={() =>
                            onChange({
                              sordner: filters.sordner === s.nummer ? "" : s.nummer,
                            })
                          }
                        >
                          {s.nummer}
                        </ChipButton>
                      );
                      return needBreak
                        ? [
                            <div
                              key={`s-br-${s.nummer}`}
                              aria-hidden
                              className="h-0 w-full shrink-0 grow-0 basis-full overflow-hidden"
                            />,
                            chip,
                          ]
                        : [chip];
                    })}
                  </div>
                )}
              </div>
            </>
          ) : null}

          {!showKundeOrdner && !showSOrdner ? (
            <p className="text-sm text-label-tertiary">
              Wähle ein Jahr oder S‑Ordner, um Ordner-Chips anzuzeigen.
            </p>
          ) : null}
        </div>

        {hasActiveFilters ? (
          <p className="text-xs text-label-tertiary lg:col-span-2">
            Filter aktiv – URL enthält die aktuelle Auswahl und kann geteilt werden.
          </p>
        ) : null}
      </div>
      ) : null}

      <OrdnerPickerModal
        open={ordnerModalOpen}
        onClose={() => setOrdnerModalOpen(false)}
        ordner={ordnerDisplay}
        loading={ordnerLoading}
        value={filters.ordner}
        onSelect={(o) => {
          onChange({ ordner: o });
          setOrdnerModalOpen(false);
        }}
      />
    </>
  );
}

function Stack({
  children,
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & { className?: string }) {
  return (
    <div className={clsx("space-y-2", className)} {...rest}>
      {children}
    </div>
  );
}

function SectionLabel({
  children,
  right,
}: {
  children: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
        {children}
      </h3>
      {right}
    </div>
  );
}

function ChipButton({
  active,
  compact,
  disabled,
  onClick,
  children,
  title,
}: {
  active?: boolean;
  compact?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={clsx(
        "shrink-0 rounded-full font-medium transition",
        compact ? "min-h-10 px-3 py-2 text-sm md:min-h-0 md:py-1.5" : "px-4 py-2 text-base",
        disabled && "cursor-not-allowed opacity-45",
        active
          ? "bg-accent text-label-on-accent shadow-sm"
          : clsx(!disabled && "hover:bg-fill", "bg-fill-tertiary text-label"),
      )}
    >
      {children}
    </button>
  );
}
